import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Avatar, Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import { Camera16Regular, Delete16Regular } from "@fluentui/react-icons";
import { api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";

const SIZE = 400;

const useStyles = makeStyles({
  row: { display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" },
  side: { display: "flex", flexDirection: "column", gap: "6px", minWidth: 0, flex: 1 },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  hint: { color: tokens.colorNeutralForeground3 },
});

/**
 * Centre-crop to a square and re-encode as JPEG in the browser. Re-encoding drops EXIF metadata
 * (camera details, GPS location), and keeps uploads small.
 */
async function toSquareJpeg(file: File): Promise<string> {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  const side = Math.min(bmp.width, bmp.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = Math.min(SIZE, side);
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, canvas.width, canvas.height);
  bmp.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

/** Trainer's profile picture: shown to clients on the sign-up link so they know it's really you. Optional. */
export function ProfilePhoto() {
  const s = useStyles();
  const { user, refresh } = useAuth();
  const t = user!.trainer!;
  const input = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (file: File) => api.put("/api/trainers/me/photo", { image: await toSquareJpeg(file) }),
    onSuccess: () => void refresh(),
    onError: (e) => setErr((e as Error).message || "Couldn't read that image — try a JPEG or PNG."),
  });
  const remove = useMutation({ mutationFn: () => api.del("/api/trainers/me/photo"), onSuccess: () => void refresh() });

  return (
    <div className={s.row}>
      <Avatar name={user!.name} image={t.photoUrl ? { src: t.photoUrl } : undefined} size={72} color="brand" />
      <div className={s.side}>
        <Text weight="semibold">Profile photo</Text>
        <Text size={200} className={s.hint}>
          Clients see it on your sign-up link, so they know it's you. Optional — without one, the sign-up page just shows your name.
        </Text>
        <div className={s.actions}>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              setErr(null);
              if (f) upload.mutate(f);
            }}
          />
          <Button size="small" icon={<Camera16Regular />} disabled={upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? "Uploading…" : t.photoUrl ? "Change photo" : "Add a photo"}
          </Button>
          {t.photoUrl && (
            <Button size="small" appearance="subtle" icon={<Delete16Regular />} disabled={remove.isPending} onClick={() => remove.mutate()}>
              Remove
            </Button>
          )}
        </div>
        {err && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
      </div>
    </div>
  );
}
